import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  BookOpen,
  Calculator,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Flag,
  Compass,
  Download,
  FileSpreadsheet,
  FileText,
  Heart,
  Languages,
  Laptop,
  Layers,
  Music,
  Palette,
  Sun,
  Utensils,
} from 'lucide-react';
import { breakKindOf } from '@/components/timetable/breakKind';
import { ExamSlotCard } from '@/components/timetable/ExamSlotCard';
import { examsInPeriod } from '@/components/timetable/examPlacement';
import { ExamDayMark, WeekExams, examsByDay } from '@/components/timetable/WeekExams';

import { Menu } from '@/components/ui/Menu';
import { PrintDocument } from '@/components/ui/PrintDocument';
import { env } from '@/config/env';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  WEEKDAY_LABEL,
  WEEKDAY_SHORT,
  periodRange,
} from '@/features/teacher/components/timetable/timetableCopy';
import { instituteToday } from '@/features/teacher/utils';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { downloadXlsx, type XlsxCell } from '@/lib/xlsx';
import type { StudentTimetable } from '@/types/student';

/**
 * The week this child's section is taught.
 *
 * Shared by the student panel (`/student/timetable`) and the parent panel
 * (`/parent/children/:uuid/timetable`), which read the same server-side week —
 * one grid, so a child and their family cannot be shown two versions of it.
 * Nothing here knows which panel it is on: the display choices and whose week
 * it is come in as props. Matching the reference design:
 * - Top Action & Week date selector bar with CSV export
 * - Full Timetable Matrix with subject cards and day highlight
 * - Subject Teacher Details 2-column key-value grid
 * - Timetable Overview metrics (Total Classes, Subjects, Working Days, Holidays)
 * - Bottom landscape banner
 */

function getSubjectTheme(subjectName: string) {
  const norm = subjectName.toLowerCase();
  if (norm.includes('math')) {
    return {
      card: 'bg-emerald-500/10 border-emerald-500/25 dark:bg-emerald-950/40 dark:border-emerald-500/30',
      iconBox: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      icon: <Calculator className="h-4 w-4" />,
    };
  }
  if (norm.includes('eng')) {
    return {
      card: 'bg-purple-500/10 border-purple-500/25 dark:bg-purple-950/40 dark:border-purple-500/30',
      iconBox: 'bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30',
      icon: <BookOpen className="h-4 w-4" />,
    };
  }
  if (
    norm.includes('evs') ||
    norm.includes('sci') ||
    norm.includes('bio') ||
    norm.includes('chem') ||
    norm.includes('phy')
  ) {
    return {
      card: 'bg-cyan-500/10 border-cyan-500/25 dark:bg-cyan-950/40 dark:border-cyan-500/30',
      iconBox: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
      icon: <Compass className="h-4 w-4" />,
    };
  }
  if (
    norm.includes('telugu') ||
    norm.includes('hindi') ||
    norm.includes('lang') ||
    norm.includes('sanskrit')
  ) {
    return {
      card: 'bg-rose-500/10 border-rose-500/25 dark:bg-rose-950/40 dark:border-rose-500/30',
      iconBox: 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border-rose-500/30',
      icon: <Languages className="h-4 w-4" />,
    };
  }
  if (
    norm.includes('comp') ||
    norm.includes('it') ||
    norm.includes('ai') ||
    norm.includes('tech')
  ) {
    return {
      card: 'bg-blue-500/10 border-blue-500/25 dark:bg-blue-950/40 dark:border-blue-500/30',
      iconBox: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30',
      icon: <Laptop className="h-4 w-4" />,
    };
  }
  if (norm.includes('music')) {
    return {
      card: 'bg-yellow-500/10 border-yellow-500/25 dark:bg-yellow-950/40 dark:border-yellow-500/30',
      iconBox: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/30',
      icon: <Music className="h-4 w-4" />,
    };
  }
  if (norm.includes('art') || norm.includes('craft') || norm.includes('draw')) {
    return {
      card: 'bg-amber-500/10 border-amber-500/25 dark:bg-amber-950/40 dark:border-amber-500/30',
      iconBox: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
      icon: <Palette className="h-4 w-4" />,
    };
  }
  if (
    norm.includes('phys') ||
    norm.includes('sport') ||
    norm.includes('pt') ||
    norm.includes('game') ||
    norm.includes('pe')
  ) {
    return {
      card: 'bg-red-500/10 border-red-500/25 dark:bg-red-950/40 dark:border-red-500/30',
      iconBox: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30',
      icon: <Activity className="h-4 w-4" />,
    };
  }
  if (
    norm.includes('life') ||
    norm.includes('skill') ||
    norm.includes('moral') ||
    norm.includes('value')
  ) {
    return {
      card: 'bg-pink-500/10 border-pink-500/25 dark:bg-pink-950/40 dark:border-pink-500/30',
      iconBox: 'bg-pink-500/20 text-pink-700 dark:text-pink-300 border-pink-500/30',
      icon: <Heart className="h-4 w-4" />,
    };
  }
  return {
    card: 'bg-indigo-500/10 border-indigo-500/25 dark:bg-indigo-950/40 dark:border-indigo-500/30',
    iconBox: 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
    icon: <BookOpen className="h-4 w-4" />,
  };
}


function downloadTimetableExcel(timetable: StudentTimetable | null) {
  if (!timetable) return;
  const days = timetable.workingDays ?? [];
  const periods = timetable.periods ?? [];
  const sectionName = timetable.section?.displayName || 'Class';

  const headers = ['Time Slot', ...days.map((d) => WEEKDAY_LABEL[d] ?? d)];
  const rows: XlsxCell[][] = [headers];

  periods.forEach((period) => {
    const timeRange = periodRange(period.startTime, period.endTime);
    if (period.kind === 'BREAK') {
      const label = period.label || 'Break';
      rows.push([timeRange, ...days.map(() => label)]);
    } else {
      const row = [
        timeRange,
        ...days.map((day) => {
          const holiday = timetable.days?.find((d) => d.dayOfWeek === day && d.holiday)?.holiday;
          if (holiday) return `Holiday: ${holiday}`;
          const entry = timetable.entries?.find(
            (e) => e.dayOfWeek === day && e.periodUuid === period.uuid,
          );
          if (!entry) return 'Free';
          return entry.teacher ? `${entry.subject.name} (${entry.teacher})` : entry.subject.name;
        }),
      ];
      rows.push(row);
    }
  });

  const cleanSection = sectionName.replace(/[^a-zA-Z0-9_-]/g, '_');
  downloadXlsx(`Timetable_${cleanSection}.xlsx`, 'Timetable', rows);
}

function TimetablePrintDocument({
  timetable,
  instituteName,
  weekDateRange,
}: {
  timetable: StudentTimetable;
  instituteName: string;
  weekDateRange: string;
}) {
  const days = timetable.workingDays ?? [];
  const periods = timetable.periods ?? [];
  const sectionName = timetable.section?.displayName || 'Class';

  return (
    <article className="print-landscape bg-white text-black w-full max-w-full p-6">
      <div className="border-b-2 border-black pb-3 text-center mb-4">
        <h1 className="text-xl font-bold tracking-tight">{instituteName}</h1>
        <p className="mt-1 text-sm font-bold uppercase tracking-wider">
          Class Timetable — {sectionName}
        </p>
        <p className="mt-0.5 text-xs text-gray-700">Week of {weekDateRange}</p>
      </div>

      <table className="w-full border-collapse border border-black text-xs">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-black p-2 text-center font-bold w-32">Time Slot</th>
            {days.map((day) => (
              <th key={day} className="border border-black p-2 text-center font-bold">
                {WEEKDAY_LABEL[day] ?? day}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {periods.map((period) => {
            const timeRange = periodRange(period.startTime, period.endTime);
            if (period.kind === 'BREAK') {
              const label = period.label || 'Break';
              return (
                <tr key={period.uuid} className="bg-slate-50">
                  <td className="border border-black p-2 text-center font-bold whitespace-nowrap">{timeRange}</td>
                  <td
                    colSpan={days.length}
                    className="border border-black p-2 text-center font-bold italic text-gray-700 tracking-wide"
                  >
                    — {label} —
                  </td>
                </tr>
              );
            }

            return (
              <tr key={period.uuid}>
                <td className="border border-black p-2 text-center font-semibold bg-slate-50 whitespace-nowrap">
                  {timeRange}
                </td>
                {days.map((day) => {
                  const holiday = timetable.days?.find((d) => d.dayOfWeek === day && d.holiday)?.holiday;
                  if (holiday) {
                    return (
                      <td key={day} className="border border-black p-2 text-center bg-rose-50/50 font-bold text-rose-800">
                        Holiday: {holiday}
                      </td>
                    );
                  }

                  const entry = timetable.entries?.find(
                    (e) => e.dayOfWeek === day && e.periodUuid === period.uuid,
                  );

                  if (!entry) {
                    return (
                      <td key={day} className="border border-black p-2 text-center text-gray-400">
                        —
                      </td>
                    );
                  }

                  return (
                    <td key={day} className="border border-black p-2 text-center">
                      <div className="font-bold text-slate-900">{entry.subject.name}</div>
                      {entry.teacher && (
                        <div className="text-[10px] text-gray-600 mt-0.5">{entry.teacher}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex items-center justify-between text-[11px] text-gray-600 border-t border-gray-300 pt-2">
        <span>Generated on {formatDate(new Date().toISOString().split('T')[0])}</span>
        <span>{sectionName} Weekly Timetable</span>
      </div>
    </article>
  );
}

interface ClassTimetableProps {
  timetable: StudentTimetable | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /**
   * Whose week this is, when the reader is not the child themselves. Set by the
   * parent panel so the empty and error copy names the child rather than
   * telling a parent *they* have not been placed in a class.
   */
  childName?: string;
  /** The student's own Settings choices; everyone else sees both. */
  showSubjectIcons?: boolean;
  showTeacherName?: boolean;
  /**
   * Told the week picked, as any day in it (`YYYY-MM-DD`), so the page can ask
   * the server for that week. Without it the picker only relabels the dates.
   */
  onWeekChange?: (weekOf: string) => void;
}

/** A picked day as the API spells it, in the reader's own calendar. */
function toWeekOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The reverse: a `YYYY-MM-DD` day as a local date, for the picker to sit on. */
function fromDayString(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

/** "2 Nov" — a calendar day the server sent, read as written, never shifted by a zone. */
function dayMonth(day: string): string {
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
    new Date(`${day}T00:00:00Z`),
  );
}

export function ClassTimetable({
  timetable,
  isLoading,
  error,
  onRetry,
  childName,
  showSubjectIcons = true,
  showTeacherName = true,
  onWeekChange,
}: ClassTimetableProps) {

  const branding = useInstituteBranding((state) => state.branding);
  const instituteName = branding?.displayName ?? env.appName;
  const [isPrintingPdf, setIsPrintingPdf] = useState(false);

  const days = timetable?.workingDays ?? [];
  const periods = timetable?.periods ?? [];
  const teachingPeriods = periods.filter((period) => period.kind === 'TEACHING');
  const hasWeek = days.length > 0 && teachingPeriods.length > 0;

  // The school's day, not the device's: a phone set to another zone, or a
  // wrong clock, put the "today" highlight and "this week" on the wrong day.
  const today = instituteToday();

  const [selectedDate, setSelectedDate] = useState<Date>(() => fromDayString(instituteToday()));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => fromDayString(instituteToday()));
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close calendar popover on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setIsCalendarOpen(false);
      }
    }
    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCalendarOpen]);

  const examsOnDay = useMemo(() => examsByDay(timetable?.days), [timetable?.days]);

  const holidayByDay = useMemo(() => {
    return new Map(
      (timetable?.days ?? [])
        .filter((day) => day.holiday !== null)
        .map((day) => [day.dayOfWeek, day.holiday as string]),
    );
  }, [timetable?.days]);

  /**
   * The days this week's timetable actually applies on. A timetable that starts
   * on a Wednesday teaches nothing on Monday and Tuesday, and its lessons must
   * not be drawn there. Missing on an older response means every day.
   */
  const coveredDays = useMemo(
    () =>
      new Set(
        (timetable?.days ?? [])
          .filter((day) => day.inTimetable !== false)
          .map((day) => day.dayOfWeek),
      ),
    [timetable?.days],
  );

  /**
   * Each column's date, as the server sent it for the week it answered. Worked
   * out here from the picker it was the device's calendar, which need not be
   * the week on screen while a new one loads, nor the school's.
   */
  const dateByDay = useMemo(
    () => new Map((timetable?.days ?? []).map((day) => [day.dayOfWeek, day.date])),
    [timetable?.days],
  );

  const weekStart = timetable?.weekStart ?? null;
  const weekEnd = timetable?.weekEnd ?? null;
  const isCurrentWeek = weekStart !== null && weekEnd !== null && weekStart <= today && today <= weekEnd;

  /** "2 Nov – 7 Nov 2026": Monday to the last working day the server drew. */
  const weekDateRange = useMemo(() => {
    const dates = (timetable?.days ?? []).map((day) => day.date).sort();
    const from = weekStart ?? dates[0];
    const to = dates.length > 0 ? dates[dates.length - 1] : weekEnd;
    if (!from || !to) return 'Choose a week';

    return `${dayMonth(from)} – ${dayMonth(to)} ${to.slice(0, 4)}`;
  }, [timetable?.days, weekStart, weekEnd]);

  const calendarWeeks = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();

    const daysList: { date: Date; isCurrentMonth: boolean }[] = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      daysList.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      daysList.push({
        date: new Date(year, month, d),
        isCurrentMonth: true,
      });
    }

    const total = Math.ceil(daysList.length / 7) * 7;
    const remaining = total - daysList.length;
    for (let d = 1; d <= remaining; d++) {
      daysList.push({
        date: new Date(year, month + 1, d),
        isCurrentMonth: false,
      });
    }

    const weeks: { date: Date; isCurrentMonth: boolean }[][] = [];
    for (let i = 0; i < daysList.length; i += 7) {
      weeks.push(daysList.slice(i, i + 7));
    }
    return weeks;
  }, [viewMonth]);

  const isWeekSelected = (week: { date: Date }[]) => {
    // The week the server answered for, once it has; the one picked until then.
    if (weekStart) return toWeekOf(week[0].date) === weekStart;

    const d = new Date(selectedDate);
    const day = d.getDay();
    const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
    const selectedMonday = new Date(d.setDate(diffToMonday)).toDateString();
    return week[0].date.toDateString() === selectedMonday;
  };

  const handleSelectDate = (d: Date) => {
    setSelectedDate(d);
    setViewMonth(new Date(d));
    setIsCalendarOpen(false);
    onWeekChange?.(toWeekOf(d));
  };

  /** A week back or forward from the one on screen — the server's week once it has answered. */
  const stepWeek = (direction: -1 | 1) => {
    const anchor = weekStart ? fromDayString(weekStart) : new Date(selectedDate);
    anchor.setDate(anchor.getDate() + direction * 7);
    handleSelectDate(anchor);
  };

  const subjectTeacherList = useMemo(() => {
    const map = new Map<string, string | null>();
    (timetable?.entries ?? []).forEach((entry) => {
      if (!map.has(entry.subject.name) || (!map.get(entry.subject.name) && entry.teacher)) {
        map.set(entry.subject.name, entry.teacher);
      }
    });
    return Array.from(map.entries()).map(([subject, teacher]) => ({
      subject,
      teacher: teacher || 'Not allotted',
    }));
  }, [timetable?.entries]);

  return (
    <div className="flex flex-col gap-5 animate-fade-in">
      {/* TOP ACTION CONTROLS BAR (CLEAN & ALIGNED) */}
      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* A week at a time, either side of the picker: the calendar is for a
            jump, and stepping through it a week at a time took three clicks. */}
        <div className="flex items-center gap-1.5">
          {onWeekChange && (
            <Button
              variant="secondary"
              size="sm"
              iconOnly
              aria-label="Previous week"
              title="Previous week"
              disabled={isLoading}
              onClick={() => stepWeek(-1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          {/* Date Range Picker Pill with Custom Popover Calendar */}
          <div className="relative" ref={calendarRef}>
            <button
              type="button"
              onClick={() => {
                setViewMonth(new Date(selectedDate));
                setIsCalendarOpen((prev) => !prev);
              }}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white dark:bg-[#1b1d34] border border-slate-200 dark:border-white/10 shadow-2xs text-sm font-bold text-slate-700 dark:text-slate-200 hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors cursor-pointer"
            >
              <Calendar className="h-4 w-4 text-indigo-500 shrink-0" />
              <span>{weekDateRange}</span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-slate-400 shrink-0 transition-transform duration-200',
                  isCalendarOpen && 'rotate-180',
                )}
              />
            </button>

            {isCalendarOpen && (
              <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 z-50 w-72 max-w-[calc(100vw-28px)] p-3.5 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200 dark:border-white/10 shadow-2xl animate-fade-in">
                {/* Month Header */}
                <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-slate-100 dark:border-white/10">
                  <h4 className="text-xs font-black text-slate-800 dark:text-slate-100">
                    {viewMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h4>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
                      }}
                      className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors cursor-pointer"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
                      }}
                      className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors cursor-pointer"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Day of Week Headers */}
                <div className="grid grid-cols-7 gap-1 text-center mb-1">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((wd, i) => (
                    <span
                      key={i}
                      className="text-[10px] font-black text-slate-400 dark:text-slate-500"
                    >
                      {wd}
                    </span>
                  ))}
                </div>

                {/* Weeks and Days Grid */}
                <div className="space-y-1">
                  {calendarWeeks.map((week, wIdx) => {
                    const isSelected = isWeekSelected(week);
                    return (
                      <div
                        key={wIdx}
                        className={cn(
                          'grid grid-cols-7 gap-1 p-0.5 rounded-lg transition-colors cursor-pointer',
                          isSelected
                            ? 'bg-indigo-500/15 dark:bg-indigo-500/25 ring-1 ring-indigo-500/30'
                            : 'hover:bg-slate-100/80 dark:hover:bg-white/5',
                        )}
                        onClick={() => handleSelectDate(week[0].date)}
                      >
                        {week.map((item, dIdx) => {
                          const isToday = toWeekOf(item.date) === today;
                          return (
                            <button
                              key={dIdx}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectDate(item.date);
                              }}
                              className={cn(
                                'h-7 w-7 mx-auto grid place-items-center rounded-md text-[11px] font-bold transition-all cursor-pointer',
                                item.isCurrentMonth
                                  ? 'text-slate-700 dark:text-slate-200'
                                  : 'text-slate-300 dark:text-slate-600',
                                isToday && 'bg-indigo-600 text-white font-black shadow-xs',
                              )}
                            >
                              {item.date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-white/10 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => handleSelectDate(fromDayString(today))}
                    className="text-[11px] font-extrabold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                  >
                    Today / This week
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCalendarOpen(false)}
                    className="text-[11px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
          {onWeekChange && (
            <Button
              variant="secondary"
              size="sm"
              iconOnly
              aria-label="Next week"
              title="Next week"
              disabled={isLoading}
              onClick={() => stepWeek(1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* This Week Badge / Button */}
        <button
          type="button"
          onClick={() => {
            setSelectedDate(fromDayString(today));
            setViewMonth(fromDayString(today));
            onWeekChange?.(today);
          }}
          className={cn(
            'inline-flex items-center px-3 py-2 rounded-xl text-sm font-extrabold transition-all cursor-pointer',
            isCurrentWeek
              ? 'bg-indigo-600/10 text-indigo-600 dark:bg-indigo-600/20 dark:text-indigo-300 border border-indigo-500/20'
              : 'bg-white dark:bg-[#1b1d34] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-white/10 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 hover:text-indigo-600',
          )}
        >
          This week
        </button>

        {/* Download Timetable Button */}
        {/* Download Timetable Dropdown Menu */}
        <Menu
          label="Download timetable options"
          align="end"
          trigger={
            <span className="inline-flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>Download Timetable</span>
              <ChevronDown className="h-3.5 w-3.5 text-indigo-500/80 shrink-0" aria-hidden />
            </span>
          }
          triggerClassName="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-indigo-500/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 text-sm font-extrabold shadow-2xs cursor-pointer bg-white dark:bg-[#1b1d34]"
          disabled={!hasWeek}
          items={[
            {
              key: 'excel',
              label: 'Excel (.xlsx)',
              description: 'Download timetable as spreadsheet',
              icon: <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
              onSelect: () => downloadTimetableExcel(timetable),
            },
            {
              key: 'pdf',
              label: 'PDF Document',
              description: 'Print or save timetable as PDF',
              icon: <FileText className="h-4 w-4 text-rose-600 dark:text-rose-400" />,
              onSelect: () => setIsPrintingPdf(true),
            },
          ]}
        />
      </div>

      {/* With a grid the papers sit in its periods; without one they still need showing. */}
      {!isLoading && !error && !hasWeek && <WeekExams days={timetable?.days} />}

      {/* A dated timetable that starts later would otherwise read as a class
          with no lessons at all. `null` is the server saying none applies;
          an older response without the key says nothing either way. */}
      {!isLoading && !error && timetable?.section && timetable.timetable === null && (
        <Alert tone="info" title="No timetable for this week">
          {timetable.upcoming ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>
                The next one applies from {formatDate(timetable.upcoming.effectiveFrom)}
                {timetable.upcoming.effectiveTo ? ` to ${formatDate(timetable.upcoming.effectiveTo)}` : ''}.
              </span>
              {onWeekChange && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (timetable.upcoming) handleSelectDate(fromDayString(timetable.upcoming.effectiveFrom));
                  }}
                >
                  Go to that week
                </Button>
              )}
            </span>
          ) : (
            'The school has not set a timetable for these dates yet.'
          )}
        </Alert>
      )}

      {/* MAIN TIMETABLE GRID CARD */}
      <div className="rounded-[24px]  bg-white p-3 shadow-[0_6px_20px_rgba(40,42,85,.05)] dark:border-white/10 dark:bg-[#1b1d34]">
        {isLoading && <SkeletonLines count={8} />}

        {!isLoading && error && (
          <div className="flex flex-col items-start gap-3">
            <Alert
              tone="critical"
              title={childName ? `Could not load ${childName}'s timetable` : 'Could not load your timetable'}
            >
              {error.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && !hasWeek && (
          <p className="py-12 text-center text-sm font-bold text-[#626579] dark:text-slate-400">
            {timetable?.section
              ? `${childName ? `${childName}'s` : 'Your'} class timetable has not been drawn up yet. It appears here as soon as the office arranges the week.`
              : childName
                ? `${childName} has not been placed in a class yet. Once the school office does, their timetable appears here.`
                : 'You have not been placed in a class yet. Once the office does, your timetable appears here.'}
          </p>
        )}

        {/* Timetable Table Grid */}
        {!isLoading && !error && hasWeek && (
          <div className="timetable-scrollbar overflow-x-auto rounded-[18px] border border-[#e2e4f0] dark:border-white/10 shadow-2xs transition-colors">
            <table className="w-full table-fixed min-w-[820px] lg:min-w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-[#e2e4f0] dark:border-white/10">
                  {/* TIME HEADER */}
                  <th className="w-28 sm:w-32 px-3 py-3 text-center bg-[#f8fafc] dark:bg-slate-900/80 border-r border-[#e2e4f0] dark:border-white/10 text-slate-600 dark:text-slate-300 font-black text-sm shrink-0">
                    Time
                  </th>

                  {/* DAYS HEADERS */}
                  {days.map((day) => {
                    const shortLabel = WEEKDAY_SHORT[day] ?? WEEKDAY_LABEL[day];
                    const date = dateByDay.get(day);
                    const isToday = date === today;
                    const isHoliday = holidayByDay.has(day);
                    const dateStr = date ? dayMonth(date) : undefined;

                    return (
                      <th
                        key={day}
                        className={cn(
                          'p-2 sm:p-2.5 text-center border-r border-[#e2e4f0] dark:border-white/10 last:border-r-0 transition-colors',
                          isHoliday
                            ? 'bg-rose-50/70 dark:bg-rose-950/25 text-rose-700 dark:text-rose-300 font-black'
                            : isToday
                              ? 'bg-indigo-50/90 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-black'
                              : 'bg-[#f8fafc] dark:bg-slate-900/80 text-slate-700 dark:text-slate-200 font-extrabold',
                        )}
                      >
                        <div className="flex flex-col items-center justify-center gap-0.5">
                          <span className="text-xs font-black tracking-wide">{shortLabel}</span>
                          {dateStr && (
                            <span
                              className={cn(
                                'text-[10px] sm:text-[12px] font-semibold',
                                isHoliday
                                  ? 'text-rose-500 dark:text-rose-400'
                                  : isToday
                                    ? 'text-indigo-600 dark:text-indigo-300'
                                    : 'text-slate-400 dark:text-slate-500',
                              )}
                            >
                              {dateStr}
                            </span>
                          )}
                          <ExamDayMark papers={examsOnDay.get(day) ?? []} />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody className="divide-y divide-[#e9e9f0] dark:divide-white/10">
                {periods.map((period, periodIndex) => {
                  if (period.kind === 'BREAK') {
                    const label = period.label || 'Break';
                    const breakKind = breakKindOf(label);
                    // Holiday columns are already covered by the first teaching row's rowSpan.
                    // The break row must only span the non-holiday columns.

                    return (
                      <tr
                        key={period.uuid}
                        className="bg-slate-50/70 dark:bg-slate-900/40 border-y border-dashed border-slate-200 dark:border-white/10"
                      >
                        <td className="px-2.5 py-2 text-center border-r border-[#e2e4f0] dark:border-white/10 font-bold text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {periodRange(period.startTime, period.endTime)}
                        </td>
                        <td colSpan={days.length} className="p-2 text-center">
                          <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-white/10 text-[11px] font-extrabold shadow-2xs">
                            {breakKind === 'LUNCH' ? (
                              <Utensils className="h-3.5 w-3.5 text-amber-500" />
                            ) : breakKind === 'ASSEMBLY' ? (
                              <Flag className="h-3.5 w-3.5 text-amber-500" />
                            ) : (
                              <Coffee className="h-3.5 w-3.5 text-amber-500" />
                            )}
                            <span>{label}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr
                      key={period.uuid}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors"
                    >
                      {/* TIME SLOT COLUMN */}
                      <td className="px-2.5 py-2 text-center border-r border-[#e2e4f0] dark:border-white/10 bg-[#f8fafc]/60 dark:bg-slate-900/40 align-middle">
                        <span className="font-bold text-xs text-slate-700 dark:text-slate-300 whitespace-nowrap block">
                          {periodRange(period.startTime, period.endTime)}
                        </span>
                      </td>

                      {days.map((day) => {
                        const holiday = holidayByDay.get(day);

                        if (holiday) {
                          // Only the very first period row renders the holiday cell.
                          // rowSpan covers ALL rows (teaching + breaks) so nothing leaks through.
                          if (periodIndex > 0) return null;

                          return (
                            <td
                              key={day}
                              rowSpan={periods.length}
                              className="p-1 sm:p-1.5 border-r border-[#e2e4f0] dark:border-white/10 last:border-r-0 align-middle bg-rose-50/30 dark:bg-rose-950/10"
                            >
                              <div className="h-full min-h-[52px] rounded-[12px] border border-dashed border-rose-200/70 dark:border-rose-800/40 bg-rose-50/50 dark:bg-rose-950/15 flex flex-col items-center justify-center gap-1 p-1.5 text-center">
                                <Sun className="h-4 w-4 text-amber-500 shrink-0" />
                                <span className="font-black text-[11px] text-rose-800 dark:text-rose-200 truncate max-w-full">
                                  {holiday}
                                </span>
                                <span className="text-[9px] font-bold text-rose-500/70 dark:text-rose-400/60">
                                  Institute closed
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Nothing on a day the timetable does not cover yet (or any more).
                        const match = coveredDays.has(day)
                          ? timetable?.entries.find(
                              (entry) => entry.dayOfWeek === day && entry.periodUuid === period.uuid,
                            )
                          : undefined;

                        // Papers sat in this period, as the teacher's week places them.
                        const examCards = examsInPeriod(examsOnDay.get(day) ?? [], {
                          timeStart: period.startTime,
                          timeEnd: period.endTime,
                        }).map((paper) => <ExamSlotCard key={paper.uuid} exam={paper} />);

                        // A free period with a paper in it shows the paper alone.
                        if (!match && examCards.length > 0) {
                          return (
                            <td
                              key={day}
                              className="p-1 sm:p-1.5 border-r border-[#e2e4f0] dark:border-white/10 last:border-r-0 align-middle min-w-0"
                            >
                              <div className="flex flex-col gap-1.5">{examCards}</div>
                            </td>
                          );
                        }

                        if (!match) {
                          return (
                            <td
                              key={day}
                              className="p-1 sm:p-1.5 border-r border-[#e2e4f0] dark:border-white/10 last:border-r-0 align-middle"
                            >
                              <div className="h-[52px] rounded-[12px] border border-dashed border-slate-200/80 bg-slate-50/40 dark:border-slate-800 dark:bg-slate-900/20 flex items-center justify-center">
                                <BookOpen className="h-3.5 w-3.5 text-slate-300/60 dark:text-slate-700/60" />
                              </div>
                            </td>
                          );
                        }

                        const subjectTheme = getSubjectTheme(match.subject.name);

                        return (
                          <td
                            key={day}
                            className="p-1 sm:p-1.5 border-r border-[#e2e4f0] dark:border-white/10 last:border-r-0 align-middle min-w-0"
                          >
                            {/* The lesson stays beside the paper, as on the teacher's week. */}
                            {examCards.length > 0 && <div className="mb-1.5 flex flex-col gap-1.5">{examCards}</div>}
                            <div
                              title={`${match.subject.name}${match.teacher ? ` • ${match.teacher}` : ''}`}
                              className={cn(
                                'px-2.5 py-1.5 rounded-[12px] border flex items-center gap-2 shadow-2xs transition-all hover:scale-[1.01] hover:shadow-sm cursor-default min-w-0 h-[52px]',
                                subjectTheme.card,
                              )}
                            >
                              {showSubjectIcons && (
                                <span
                                  className={cn(
                                    'grid h-7.5 w-7.5 place-items-center rounded-lg border shrink-0',
                                    subjectTheme.iconBox,
                                  )}
                                  aria-hidden
                                >
                                  {subjectTheme.icon}
                                </span>
                              )}
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <h4 className="font-bold text-sm text-slate-700 dark:text-slate-100 leading-tight truncate whitespace-nowrap">
                                  {match.subject.name}
                                </h4>
                                {showTeacherName && (
                                  <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-0.5 truncate whitespace-nowrap">
                                    {match.teacher ?? 'Teacher not allotted'}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* BOTTOM CARDS SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        {/* CARD 1: SUBJECT TEACHER DETAILS */}
        <div className="lg:col-span-8 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_6px_20px_rgba(40,42,85,.05)] dark:border-white/10 dark:bg-[#1b1d34] flex flex-col justify-between gap-4">
          <div>
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3 mb-3">
              <h4 className="text-[14px] text-slate-700 font-black text-[#1b2142] dark:text-[#f2f4ff]">
                Subject Teacher Details
              </h4>
              <span className="text-[11px] font-extrabold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-1 rounded-pill border border-indigo-200/60 dark:border-indigo-500/20">
                {subjectTeacherList.length} Subjects
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              {subjectTeacherList.length === 0 ? (
                <p className="text-slate-400 dark:text-slate-500 italic">
                  No subject details available
                </p>
              ) : (
                subjectTeacherList.map(({ subject, teacher }) => (
                  <div key={subject} className="flex items-baseline justify-between gap-2 py-0.5">
                    <span className="font-bold text-[12px] text-slate-700 dark:text-slate-200 truncate">
                      {subject}
                    </span>
                    <span className="text-slate-400 text-[12px] dark:text-slate-500 shrink-0">:</span>
                    <span className="font-semibold text-slate-500 text-[12px] dark:text-slate-400 truncate text-right">
                      {teacher}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* CARD 2: TIMETABLE OVERVIEW */}
        <div className="lg:col-span-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_6px_20px_rgba(40,42,85,.05)] dark:border-white/10 dark:bg-[#1b1d34] flex flex-col justify-between gap-4">
          <div>
            <div className="border-b border-slate-100 dark:border-white/10 pb-3 mb-3">
              <h4 className="text-[14px] text-slate-700 font-black text-[#1b2142] dark:text-[#f2f4ff]">
                Timetable Overview
              </h4>
            </div>

            <div className="grid grid-cols-1 gap-2.5 text-xs">
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-purple-500/10 dark:bg-purple-950/40 border border-purple-500/20">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-purple-500 text-white font-black shrink-0">
                  <BookOpen className="h-4 w-4" />
                </span>
                <div className="min-w-0 leading-wide">
                  <p className="text-[13px]  font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
                    Periods per day
                  </p>
                  {/* The teaching rows of the grid below, breaks left out — so
                      the figure is the one a child counts down the table. It
                      was the week's lessons, which read as a period count and
                      never matched the rows. */}
                  <p className="text-base font-black text-purple-700 dark:text-purple-300 ">
                    {teachingPeriods.length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/20">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500 text-white font-black shrink-0">
                  <Layers className="h-4 w-4" />
                </span>
                <div className="min-w-0 leading-wide">
                  <p className="text-[13px]  font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
                    Subjects
                  </p>
                  <p className="text-base font-black text-emerald-700 dark:text-emerald-300 ">
                    {subjectTeacherList.length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-amber-500/10 dark:bg-amber-950/40 border border-amber-500/20">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-500 text-white font-black shrink-0">
                  <Calendar className="h-4 w-4" />
                </span>
                <div className="min-w-0 leading-wide">
                  <p className="text-[13px]  font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
                    Working Days
                  </p>
                  <p className="text-base font-black text-amber-700 dark:text-amber-300 ">
                    {days.length}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-rose-500/10 dark:bg-rose-950/40 border border-rose-500/20">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-rose-500 text-white font-black shrink-0">
                  <Sun className="h-4 w-4" />
                </span>
                <div className="min-w-0 leading-wide">
                  <p className="text-[13px]  font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
                    Holidays
                  </p>
                  <p className="text-base font-black text-rose-700 dark:text-rose-300 ">
                    {holidayByDay.size}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM LANDSCAPE BANNER */}
      <div className="rounded-[24px] overflow-hidden shadow-sm">
        <img
          src="/student-overview/schedule-scene.png"
          alt=""
          className="w-full h-24 sm:h-28 object-cover object-top"
        />
      </div>

      {isPrintingPdf && timetable && (
        <PrintDocument
          isOpen={isPrintingPdf}
          onFinished={() => setIsPrintingPdf(false)}
          fileName={`Timetable_${(timetable.section?.displayName || 'Class').replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          landscape
        >
          <TimetablePrintDocument
            timetable={timetable}
            instituteName={instituteName}
            weekDateRange={weekDateRange}
          />
        </PrintDocument>
      )}
    </div>
  );
}
